import React from "react";
import { useTranslation } from "react-i18next";
import { CalendarItem } from "../../features/calendar/models/CalendarItem";
import { ClosedDay } from "../../features/closed-days/models/ClosedDay";
import { OccurrenceStatus } from "../../features/sessions/models/OccurrenceStatus";
import { isAttendanceUnrecorded } from "../../features/calendar/utils/calendar-attention";

interface CalendarSummaryProps {
  items: CalendarItem[];
  closedByDate: Map<string, ClosedDay>;
}

/**
 * State of the visible range at a glance, as middot-separated counts. Only the
 * parts that are non-zero appear, so a quiet week stays quiet.
 */
const CalendarSummary: React.FC<CalendarSummaryProps> = ({ items, closedByDate }) => {
  const { t } = useTranslation();
  const today = new Date();

  const scheduled = items.filter((item) => item.status !== OccurrenceStatus.Cancelled);
  const unrecorded = scheduled.filter((item) => isAttendanceUnrecorded(item, today)).length;
  const cancelled = items.length - scheduled.length;

  if (items.length === 0 && closedByDate.size === 0) {
    return null;
  }

  const parts = [
    t("calendar.summary.sessions", { count: scheduled.length }),
    unrecorded > 0 ? t("calendar.summary.unrecorded", { count: unrecorded }) : null,
    cancelled > 0 ? t("calendar.summary.cancelled", { count: cancelled }) : null,
    closedByDate.size > 0 ? t("calendar.summary.closedDays", { count: closedByDate.size }) : null,
  ].filter(Boolean);

  return (
    <p className="mt-3 text-sm text-grey-30 dark:text-grey-70">{parts.join(" · ")}</p>
  );
};

export default CalendarSummary;
